import { useState, useEffect, useCallback, forwardRef } from 'react'
import useField from 'components/form/useField'
import styled, { css } from 'styled-components/macro'
import { button } from 'styles/mixins/textstyles'
import { BasicButton } from 'components/layout/buttons'
import ResizeTextarea from 'react-autosize-textarea'
import { body1, caption } from 'styles/mixins/textstyles'
import * as formatter from 'components/form/format'
import * as validations from 'components/form/validations'
import Checkbox from 'components/form/NewCheckbox'
import Select from 'components/form/NewSelect'
import { RadioGroup, RadioElement } from 'components/form/NewRadio'
import InputAddress from 'components/form/InputAddress'
import { useLocalization } from 'localization'

const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)

function mergeValidations(a, b) {
  if (!a) {
    return b
  }
  if (!b) {
    return a
  }
  return b.and(a)
}

const Container = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid
    ${(props) => {
      if (props.error) {
        return props.theme.colors.errorBase
      } else if (props.focused) {
        return props.theme.colors.primaryBase
      } else {
        return props.theme.colors.greyLight
      }
    }};
`

const FieldStyle = css`
  ${(props) => body1(props.theme.colors.black)}
  width: 100%;
  border: none;
  border-radius: 0;
  padding: 0;
  background: none;
  line-height: 2.2rem;

  ::placeholder {
    color: ${(props) => props.theme.colors.greyBase};
  }

  :focus {
    outline: none;

    ::placeholder {
      color: ${(props) => props.theme.colors.primaryBase};
    }
  }

  &[disabled] {
    color: ${(props) => props.theme.colors.greyLight};
    -webkit-text-fill-color: ${(props) => props.theme.colors.greyLight};
    ${() => (iOS ? 'opacity: 1;' : '')}
  }
`

const Textarea = styled(ResizeTextarea)`
  resize: none;
  ${FieldStyle}
`

const Input = styled.input`
  ${FieldStyle}
`

const HideShowLabel = styled.div`
  ${(props) => button(props.theme.colors.primaryBase)}

  position: absolute;
  right: 0;
  bottom: 0.3rem;
  cursor: pointer;
`

const LabelText = styled.label`
  ${(props) => caption(props.theme.colors.black, 'medium')}
  display: block;
  text-align: left;
  color: ${(props) => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: ${(props) => (props.tight ? '.2rem' : '1rem')};
  padding-bottom: 0.1rem;

  ${(props) =>
    props.focused &&
    css`
      color: ${(props) => props.theme.colors.primaryBase};
    `}

  ${(props) =>
    props.error &&
    css`
      color: ${(props) => props.theme.colors.errorBase};
    `}
`

const Label = (props) => {
  const { label, error, value, focused, tight, id, alwaysShowActiveLabel } = props
  const text = error || (value && label) || (alwaysShowActiveLabel && label) || '\u00A0'
  return (
    <LabelText focused={focused} error={error} tight={tight} id={id}>
      {text}
    </LabelText>
  )
}

const defaultOutputFormat = ({ raw }) => raw

function handleEmptyDefault(value) {
  if (typeof value === 'undefined' || value === null) {
    return ''
  }
  return value
}

const getValidateCb = (props) => (!props.disabled && props.validate ? props.validate : undefined)

const TextField = forwardRef((props, ref) => {
  const {
    name,
    defaultValue,
    outputFormat = defaultOutputFormat,
    activeLabel,
    label,
    type,
    alwaysShowActiveLabel = false,
    ...rest
  } = props
  const [text, setText] = useState(
    formatter.applyFormat(props.format || formatter.defaultFormat, handleEmptyDefault(defaultValue))
  )
  const validate = getValidateCb(props)
  const { value, setValue, error, revalidate, id } = useField({ name, validate, defaultValue: text.raw })
  const [hidden, setHidden] = useState(props.password)
  const [focused, setFocused] = useState(false)
  const [changed, setChanged] = useState(false)
  const floatingLabel = (!!value && (activeLabel || label)) || (alwaysShowActiveLabel && activeLabel)
  const revalidateField = useCallback(revalidate, [revalidate])

  useEffect(() => {
    setValue(outputFormat(text), changed)
    revalidateField()
  }, [outputFormat, setValue, text, changed, revalidateField])

  const handleChange = (event) => {
    const newText = formatter.applyFormat(props.format || formatter.defaultFormat, event.target.value)
    setChanged(true)
    setText(newText)

    if (rest.onChange) {
      event.persist()
      rest.onChange(event)
    }
  }

  const handleFocus = (event) => {
    setFocused(true)
    if (rest.onFocus) {
      event.persist()
      rest.onFocus(event)
    }
  }

  const handleBlur = (event) => {
    setFocused(false)
    revalidate()
    if (rest.onBlur) {
      event.persist()
      rest.onBlur(event)
    }
  }

  const toggleHidden = () => setHidden((hidden) => !hidden)

  const Comp = type === 'textarea' ? Textarea : Input

  return (
    <Container focused={focused} error={error} ref={props.containerRef}>
      <Label
        id={`${id || rest.id}-label`}
        focused={focused}
        label={floatingLabel}
        value={value}
        error={error}
        tight={props.tight}
        alwaysShowActiveLabel={alwaysShowActiveLabel}
      />
      <Comp
        id={id}
        ref={ref}
        {...rest}
        placeholder={label || rest.placeholder}
        type={type !== 'textarea' && hidden ? 'password' : type}
        value={text.formatted}
        error={error}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {props.password && (
        <HideShowLabel id={'foo'} onClick={toggleHidden}>
          {hidden ? 'Show' : 'Hide'}
        </HideShowLabel>
      )}
    </Container>
  )
})

TextField.Textarea = (props) => <TextField type="textarea" format={formatter.multiline} {...props} />

TextField.Phone = (props) => (
  <TextField
    type="tel"
    format={formatter.phoneFormat}
    {...props}
    validate={mergeValidations(props.validate, validations.phone)}
  />
)

TextField.Email = (props) => (
  <TextField format={formatter.emailFormat} {...props} validate={mergeValidations(props.validate, validations.email)} />
)

TextField.Name = (props) => <TextField format={formatter.nameFormat} {...props} />

TextField.Text = (props) => <TextField {...props} validate={props.validate} />

TextField.Initials = (props) => (
  <TextField
    format={formatter.initialsFormat}
    {...props}
    validate={mergeValidations(props.validate, validations.minLength(2))}
  />
)

const FourDigitCodeTextField = (props) => {
  const { t } = useLocalization()
  const message = t('dweb_validation_must_be_exactly_4_digits')

  return (
    <TextField
      type="tel"
      format={formatter.fourDigitFormat}
      {...props}
      validate={mergeValidations(
        props.validate,
        validations.minLength(4).withMessage(message).and(validations.maxLength(4).withMessage(message))
      )}
    />
  )
}

TextField.FourDigitCode = FourDigitCodeTextField

TextField.Tracking = (props) => (
  <TextField
    format={formatter.trackingFormat}
    {...props}
    validate={mergeValidations(props.validate, validations.minLength(16).and(validations.maxLength(16)))}
  />
)

TextField.CreditCard = (props) => <TextField type="tel" format={formatter.ccFormat} {...props} />

TextField.Integer = (props) => <TextField type="tel" format={formatter.integerFormat} {...props} />

TextField.Decimal = (props) => (
  <TextField
    type="tel"
    format={formatter.decimalFormat}
    {...props}
    validate={mergeValidations(props.validate, validations.decimal.and(validations.decimalSpaces))}
  />
)

const DateTextField = (props) => {
  const { t } = useLocalization()

  return (
    <TextField
      type="tel"
      activeLabel={t('dweb_common_date_format_mm_dd_yyyy')}
      format={formatter.dateFormat}
      outputFormat={({ formatted }) => {
        return formatted.replace(/\s/g, '')
      }}
      {...props}
      validate={mergeValidations(props.validate, validations.date.and(validations.validDate))}
    />
  )
}

TextField.Date = DateTextField

const MonthYearTextField = (props) => {
  const { t } = useLocalization()

  return (
    <TextField
      type="tel"
      activeLabel={t('dweb_common_date_format_mm_yyyy')}
      format={formatter.monthYearFormat}
      outputFormat={({ formatted }) => {
        return formatted.replace(/\s/g, '')
      }}
      {...props}
      validate={mergeValidations(props.validate, validations.monthYear.and(validations.validMonthYear))}
    />
  )
}

TextField.MonthYear = MonthYearTextField

const EINTextField = (props) => {
  const { t } = useLocalization()

  return (
    <TextField
      type="tel"
      format={formatter.einFormat}
      maxLength={10}
      {...props}
      validate={mergeValidations(
        props.validate,
        validations.ein.and(validations.minLength(9).withMessage(t('dweb_validation_invalid_ein_must_be_9_digits')))
      )}
    />
  )
}

TextField.EIN = EINTextField

TextField.RadioGroup = RadioGroup
TextField.RadioElement = RadioElement
TextField.Checkbox = Checkbox
TextField.Select = Select

const Submit = (props) => {
  const idName = props.name ? `${props.name}-submit` : 'submit'
  const { id, anyErrors, formLoading } = useField({ name: idName, defaultValue: props.value })
  const disabled = formLoading || props.disabled || anyErrors
  return (
    <BasicButton id={id} mode="primary" type="submit" {...props} disabled={disabled}>
      {props.children || props.value}
    </BasicButton>
  )
}
TextField.Submit = styled(Submit)`
  margin-top: ${(props) => props.marginTop || '3.2rem'};
`

const Hidden = (props) => {
  const { name, validate, value: defaultValue, ...rest } = props
  const { value, id } = useField({ name, validate, defaultValue })
  return <input id={rest.id || id} type="hidden" name={name} value={value} {...rest} />
}
const HiddenFix = (props) => {
  // Make sure input.hidden updates when value changes.
  const id = props.id
  const key = props.value || 'empty'
  return <Hidden key={`${id}${key}`} {...props} />
}
TextField.Hidden = HiddenFix

TextField.Address = InputAddress

export default TextField
